import { cs } from '../../lib/currency';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, CheckCircle2, XCircle, ShieldCheck, Receipt, CreditCard } from 'lucide-react';

const API_URL = (typeof window !== 'undefined' && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) || '';

const PublicPay = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const returnStatus = searchParams.get('status'); // 'success' | 'cancelled'

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [paying, setPaying] = useState(false);
  const [method, setMethod] = useState('card'); // 'card' (+surcharge) | 'us_bank_account' (ACH, no fee)

  // FastAPI `detail` may be a string OR a structured object (e.g. the 409
  // duplicate_payment_in_flight guard returns {code, message, ...}). Rendering
  // an object as a React child crashes the page (React error #31 -> blank),
  // so always coerce to a readable string.
  const errText = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object') return detail.message || detail.detail || fallback;
    return String(detail);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/public/pay/${token}`);
      const d = await r.json();
      if (!r.ok) {
        setError(errText(d?.detail, 'This payment link is invalid or has expired.'));
      } else {
        setSummary(d);
      }
    } catch (e) {
      setError('Could not load this invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handlePay = async () => {
    setPaying(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/public/pay/${token}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method }),
      });
      const d = await r.json();
      if (!r.ok || !d?.checkout_url) {
        setError(errText(d?.detail, 'Could not start the payment. Please try again.'));
        setPaying(false);
        return;
      }
      window.location.href = d.checkout_url;
    } catch (e) {
      setError('Could not start the payment. Please try again.');
      setPaying(false);
    }
  };

  const academy = summary?.academy_name || 'Academy';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="public-pay-page">
      <Card className="w-full max-w-md shadow-xl border-slate-200">
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-500 leading-none">Secure payment</p>
              <p className="text-base font-semibold text-slate-900" data-testid="public-pay-academy">{academy}</p>
            </div>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-10 text-slate-500" data-testid="public-pay-loading">
              <Loader2 className="h-6 w-6 animate-spin mb-3" />
              <p className="text-sm">Loading your invoice…</p>
            </div>
          )}

          {!loading && returnStatus === 'success' && (
            <div className="flex flex-col items-center text-center py-6" data-testid="public-pay-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-3" />
              <h2 className="text-xl font-semibold text-slate-900">Payment received</h2>
              <p className="text-sm text-slate-500 mt-2">Thank you! A receipt has been emailed to you. You can close this page.</p>
            </div>
          )}

          {!loading && returnStatus !== 'success' && error && (
            <div className="flex flex-col items-center text-center py-6" data-testid="public-pay-error">
              <XCircle className="h-12 w-12 text-rose-500 mb-3" />
              <h2 className="text-lg font-semibold text-slate-900">Unable to continue</h2>
              <p className="text-sm text-slate-500 mt-2">{error}</p>
              <Button variant="outline" className="mt-5" onClick={load} data-testid="public-pay-retry">Try again</Button>
            </div>
          )}

          {!loading && returnStatus !== 'success' && !error && summary && summary.already_paid && (
            <div className="flex flex-col items-center text-center py-6" data-testid="public-pay-already-paid">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-3" />
              <h2 className="text-xl font-semibold text-slate-900">Already paid</h2>
              <p className="text-sm text-slate-500 mt-2">This invoice has already been settled. No further action is needed.</p>
            </div>
          )}

          {!loading && returnStatus !== 'success' && !error && summary && !summary.already_paid && (
            <div data-testid="public-pay-ready">
              {returnStatus === 'cancelled' && (
                <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" data-testid="public-pay-cancelled">
                  Payment was cancelled. You can try again below.
                </div>
              )}
              <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5">
                <p className="text-sm text-slate-500">{summary.description || 'Outstanding invoice'}</p>
                {summary.parent_name && (
                  <p className="text-xs text-slate-400 mt-1">For {summary.parent_name}</p>
                )}
                {Array.isArray(summary.line_items) && summary.line_items.length > 0 && (
                  <div className="mt-3 space-y-1 text-xs border-t border-slate-100 pt-3" data-testid="public-pay-line-items">
                    {summary.line_items.map((li, idx) => (
                      <div key={idx} className="flex justify-between gap-3 text-slate-600">
                        <span className="truncate">{li.label}</span>
                        <span className="shrink-0 font-medium text-slate-800">{cs()}{Number(li.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(() => {
                  const base = typeof summary.ach_total === 'number' ? summary.ach_total : summary.amount;
                  const hasSurcharge = summary.cc_surcharge_enabled && typeof summary.surcharge_amount === 'number' && summary.surcharge_amount > 0;
                  const total = method === 'card' && hasSurcharge ? summary.card_total : base;
                  return (
                    <>
                      <p className="text-3xl font-bold text-slate-900 mt-3" data-testid="public-pay-amount">
                        {cs()}{Number(total || 0).toFixed(2)}
                      </p>
                      {hasSurcharge && (
                        <div className="mt-3 space-y-1 text-xs text-slate-500 border-t border-slate-100 pt-3" data-testid="public-pay-breakdown">
                          <div className="flex justify-between"><span>Invoice amount</span><span>{cs()}{Number(base || 0).toFixed(2)}</span></div>
                          <div className="flex justify-between">
                            <span>Card fee ({summary.cc_surcharge_percent}%)</span>
                            <span className={method === 'card' ? 'text-slate-700' : 'text-slate-400 line-through'}>
                              +{cs()}{Number(summary.surcharge_amount || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between font-semibold text-slate-900 pt-1">
                            <span>You pay</span><span>{cs()}{Number(total || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {summary.cc_surcharge_enabled && !summary.card_only && typeof summary.surcharge_amount === 'number' && summary.surcharge_amount > 0 && (
                <div className="mb-5" data-testid="public-pay-method-select">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Payment method</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setMethod('us_bank_account')}
                      className={`rounded-lg border p-3 text-left transition-colors ${method === 'us_bank_account' ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-400' : 'border-slate-200 hover:bg-slate-50'}`}
                      data-testid="public-pay-method-ach"
                    >
                      <span className="block text-sm font-semibold text-slate-900">Bank (ACH)</span>
                      <span className="block text-xs text-emerald-700 font-medium mt-0.5">No fee · save {cs()}{Number(summary.surcharge_amount).toFixed(2)}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMethod('card')}
                      className={`rounded-lg border p-3 text-left transition-colors ${method === 'card' ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-400' : 'border-slate-200 hover:bg-slate-50'}`}
                      data-testid="public-pay-method-card"
                    >
                      <span className="block text-sm font-semibold text-slate-900">Card</span>
                      <span className="block text-xs text-slate-500 mt-0.5">+{summary.cc_surcharge_percent}% card fee</span>
                    </button>
                  </div>
                  {method === 'card' && (
                    <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1" data-testid="public-pay-ach-nudge">
                      <ShieldCheck className="h-3.5 w-3.5" /> Pay by bank to avoid the {cs()}{Number(summary.surcharge_amount).toFixed(2)} card fee.
                    </p>
                  )}
                </div>
              )}

              <Button
                className="w-full h-12 text-base bg-slate-900 hover:bg-slate-800"
                onClick={handlePay}
                disabled={paying}
                data-testid="public-pay-now-button"
              >
                {paying ? (
                  <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Redirecting…</>
                ) : (
                  <><CreditCard className="h-5 w-5 mr-2" /> Pay now</>
                )}
              </Button>

              <div className="flex items-center justify-center gap-1.5 mt-4 text-xs text-slate-400">
                <ShieldCheck className="h-3.5 w-3.5" />
                <span>Payments are processed securely by Stripe</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicPay;
